// config-templates-vm.ts — PURE decision surfaces for the configuration
// templates screens (owner item 65, PR 2). No DOM, no fetch, no AG Grid —
// the create-release.ts precedent: the acceptance harness bundles THIS
// module alone and drills every judgment the page makes against the REAL
// catalog served by a live console, so the editor's behavior is measured
// without a browser. config-templates.ts (the mount) renders what these
// functions decide and nothing else.
//
// The catalog arrives from GET /api/config-catalog — the server module
// (src/server/config-catalog.ts) is the one authority; these types mirror
// only what the client reads.

// Helm settings re-implementation (owner ruling 2026-09-08): the schema types
// and every editor judgment below — shownValue / changedFromDefault / setDraft
// / sparseFromDraft / pendingDiff / parseControlValue / displayValue / resetVm
// / noDefaultCount / stepperVm / stepNumber / filterCatalog / sectionNav /
// overrideCount / reachNote — moved VERBATIM to the shared client layer
// (pi-stack/src/client/config-editor-vm.ts) so the Helm's settings tab and this
// editor decide identically. They are re-exported here under their historical
// names, so this module stays the ONE bundle the headless drill measures. What
// remains authored here is the LIST/history/picker half, bound to the
// /api/config-templates row shape.
export {
  changedFromDefault, displayValue, filterCatalog, noDefaultCount, overrideCount, parseControlValue,
  pendingDiff, reachNote, resetVm, sectionNav, setDraft, shownValue, sparseFromDraft, stepNumber, stepperVm,
  valuesPath,
  type CfgSection as CatSection, type CfgVariable as CatVariable, type FilteredSection, type PendingChange,
  type ResetVm, type SectionNavEntry, type SectionsDoc, type VarType,
} from 'pi-stack/client/config-editor-vm.js';
import { displayValue, type CfgSection as CatSection, type CfgVariable as CatVariable, type SectionsDoc } from 'pi-stack/client/config-editor-vm.js';


export interface TemplateRow {
  id: string;
  name: string;
  description: string;
  sections: SectionsDoc;
  is_default: boolean;
  disabled: boolean;
  created_at: string;
  updated_at: string;
}

const stable = (v: unknown): string => JSON.stringify(v) ?? 'null';

// ---------------------------------------------------------------------------
// List screen

/** Badges for a template row, in render order. */
export function templateBadges(t: Pick<TemplateRow, 'is_default' | 'disabled'>): string[] {
  const out: string[] = [];
  if (t.is_default) out.push('Default');
  if (t.disabled) out.push('Disabled');
  return out;
}

/** Which row actions are OFFERED (the server still refuses; the client just
 * never paints a button that can only 4xx — the users.ts doctrine).
 * Encodes: the default cannot be disabled; a disabled template cannot become
 * the default; enable only shows on disabled rows. */
export function rowActions(t: Pick<TemplateRow, 'is_default' | 'disabled'>, canManage: boolean): string[] {
  if (!canManage) return ['edit']; // read-only open — the editor renders disabled controls
  const out = ['edit', 'clone'];
  if (!t.is_default && !t.disabled) out.push('set-default');
  if (!t.is_default && !t.disabled) out.push('disable');
  if (t.disabled) out.push('enable');
  return out;
}

export interface TemplateRowMenuEntry {
  id: 'edit' | 'clone' | 'history' | 'set-default' | 'disable' | 'enable';
  label: string;
  icon: string;
  enabled: boolean;
  danger?: boolean;
  tip?: string;
}

/** The templates grid's row context menu (bridge-ui-polish item E): the
 * page's verbs with the WHY on every disabled entry — the release grid's
 * stable-roster doctrine. Availability mirrors rowActions above (ONE
 * gating), plus View Change Log…, which is open to every viewer the page
 * admits (history is read-only). The machinery appends the generic Copy
 * Value after these. */
export function templateRowMenuSpec(t: TemplateRow, canManage: boolean): TemplateRowMenuEntry[] {
  const acts = new Set(rowActions(t, canManage));
  const noManage = 'Requires the manage-templates capability';
  const out: TemplateRowMenuEntry[] = [
    canManage
      ? { id: 'edit', label: 'Open in Editor', icon: 'pencil', enabled: true }
      : { id: 'edit', label: 'Open (read-only)', icon: 'eye', enabled: true },
    {
      id: 'clone', label: 'Clone…', icon: 'copy', enabled: acts.has('clone'),
      ...(acts.has('clone') ? {} : { tip: noManage }),
    },
    { id: 'history', label: 'View Change Log…', icon: 'history', enabled: true },
    {
      id: 'set-default', label: 'Set as Default', icon: 'star', enabled: acts.has('set-default'),
      ...(acts.has('set-default') ? {} : {
        tip: t.is_default ? 'Already the default'
          : t.disabled ? 'A disabled template cannot be the default' : noManage,
      }),
    },
    t.disabled
      ? {
          id: 'enable', label: 'Enable', icon: 'circle-check', enabled: acts.has('enable'),
          ...(acts.has('enable') ? {} : { tip: noManage }),
        }
      : {
          id: 'disable', label: 'Disable', icon: 'ban', enabled: acts.has('disable'), danger: true,
          ...(acts.has('disable') ? {} : {
            tip: t.is_default
              ? 'The default template cannot be disabled — set another default first'
              : noManage,
          }),
        },
  ];
  return out;
}

/** Scroll-spy: which section owns the viewport. `positions` are the VISIBLE
 * sections in document order with content-relative tops. The active section
 * is the last one whose top has crossed the focus band (a fixed line below
 * the viewport top — capped so short panes still spy sensibly); scrolled to
 * the bottom, the LAST visible section wins even when its top never reaches
 * the band (a short final section must be selectable by scroll). */
// Styles × Themes PR-3c: the band math and the constant MOVED to the shared
// primitive (pi-stack/src/client/feedback-nav.ts, `activeSection` /
// `SPY_BAND_MAX_PX`) so the product has ONE copy of the scroll-spy decision.
// Re-exported here because config-templates-ui.test.mjs and this module's own
// consumers import it from this vm — the seam stays, the implementation is
// the primitive's.
export { activeSection, SPY_BAND_MAX_PX } from 'pi-stack/client/feedback-nav.js';

// ---------------------------------------------------------------------------
// Create-release picker (PR 3)

export interface TemplatePickerVm {
  /** Options in render order: the default first, then by name. The list the
   * server serves is already the SELECTABLE list — disabled templates never
   * reach this function (they are filtered at the query, not here). */
  options: { id: string; label: string; isDefault: boolean }[];
  /** Preselected: the default template. Null only when the list is empty
   * (a pre-migration server) — the picker then hides and the server's own
   * default resolution seeds the release. */
  selectedId: string | null;
}

export function templatePickerVm(rows: Pick<TemplateRow, 'id' | 'name' | 'is_default'>[]): TemplatePickerVm {
  const sorted = [...rows].sort((a, b) =>
    a.is_default !== b.is_default ? (a.is_default ? -1 : 1) : a.name.localeCompare(b.name));
  const def = sorted.find((r) => r.is_default) ?? sorted[0] ?? null;
  return {
    options: sorted.map((r) => ({
      id: r.id,
      label: r.is_default ? `${r.name} (default)` : r.name,
      isDefault: r.is_default,
    })),
    selectedId: def?.id ?? null,
  };
}

// ---------------------------------------------------------------------------
// Audit history (PR 3)

export interface AuditRowVm {
  when: string;
  actor: string;
  action: string;
  lines: string[];
}

/** Render an audit row for the History view: one headline per row, one
 * old -> new line per changed field, values through the catalog's display
 * rules when the path still resolves (a retired variable's change renders
 * raw JSON — history outlives the catalog). */
export function auditRowVm(
  catalog: CatSection[],
  row: { at: string; actor_name: string; action: string; changes: { path: string; old: unknown; new: unknown }[] },
): AuditRowVm {
  const byPath = new Map<string, CatVariable>();
  for (const s of catalog) for (const v of s.variables) byPath.set(`values.${s.id}.${v.key}`, v);
  const show = (path: string, value: unknown): string => {
    const v = byPath.get(path);
    return v ? displayValue(v, value) : JSON.stringify(value) ?? 'null';
  };
  return {
    when: row.at,
    actor: row.actor_name || '(unknown)',
    action: row.action,
    lines: (row.changes ?? []).map((c) => {
      const v = byPath.get(c.path);
      const label = v ? v.label : c.path;
      return `${label}: ${show(c.path, c.old)} -> ${show(c.path, c.new)}`;
    }),
  };
}
